import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, onSnapshot, query, orderBy, deleteDoc, addDoc, updateDoc } from 'firebase/firestore';
import { Logo } from '../components/Logo';

function LokisChamber() {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [flaggedItems, setFlaggedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [contentFilterSettings, setContentFilterSettings] = useState({});

  useEffect(() => {
    loadContentFilterSettings();
    setupFlaggedItemsListener();
  }, [currentUser]);

  useEffect(() => {
    filterItems();
  }, [flaggedItems, searchTerm]);

  // Load content filter settings from Firebase
  const loadContentFilterSettings = async () => {
    if (!currentUser) return;
    
    try {
      const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'preferences');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        setContentFilterSettings({
          enableContentFiltering: settings.enableContentFiltering ?? true,
          flagOverGrade8: settings.flagOverGrade8 ?? true,
          flagSexualContent: settings.flagSexualContent ?? true,
          flagControversialTopics: settings.flagControversialTopics ?? true,
          flagViolence: settings.flagViolence ?? false,
          flagLanguage: settings.flagLanguage ?? false,
          enableDuplicateChecking: settings.enableDuplicateChecking ?? false
        });
      } else {
        // Default settings
        setContentFilterSettings({
          enableContentFiltering: true,
          flagOverGrade8: true,
          flagSexualContent: true,
          flagControversialTopics: true,
          flagViolence: false,
          flagLanguage: false,
          enableDuplicateChecking: false
        });
      }
    } catch (error) {
      console.error('Error loading content filter settings:', error);
    }
  };

  // Set up real-time listener for flagged items
  const setupFlaggedItemsListener = () => {
    if (!currentUser) return;

    try {
      const flaggedRef = collection(db, 'users', currentUser.uid, 'flaggedBooks');
      const flaggedQuery = query(flaggedRef, orderBy('flaggedAt', 'desc'));
      
      // Set up real-time listener
      const unsubscribe = onSnapshot(flaggedQuery, (snapshot) => {
        const items = [];
        snapshot.forEach((doc) => {
          items.push({
            id: doc.id, // Store Firebase document ID
            ...doc.data()
          });
        });
        setFlaggedItems(items);
        setLoading(false);
      });

      // Cleanup listener on unmount
      return () => unsubscribe();
    } catch (error) {
      console.error('Error setting up flagged items listener:', error);
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = [...flaggedItems];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.title?.toLowerCase().includes(term) ||
        item.author?.toLowerCase().includes(term) ||
        item.subjects?.some(subject => subject.toLowerCase().includes(term))
      );
    }

    setFilteredItems(filtered);
  };

  const approveBook = async (documentId) => {
    try {
      // Get the flagged book data
      const flaggedRef = doc(db, 'users', currentUser.uid, 'flaggedBooks', documentId);
      const flaggedDoc = await getDoc(flaggedRef);
      
      if (!flaggedDoc.exists()) {
        console.error('Flagged book not found');
        return;
      }
      
      const bookData = flaggedDoc.data();
      
      // Get current barcode from user profile
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      const lastBarcodeUsed = userDoc.data()?.lastBarcodeUsed || 125863;
      const nextBarcode = lastBarcodeUsed + 1;
      
      // Replace temporary barcode with real barcode
      const approvedBook = {
        ...bookData,
        barcode: bookData.barcode.startsWith('PENDING-') ? nextBarcode.toString() : bookData.barcode,
        approvedAt: new Date(),
        status: 'approved',
        reviewStatus: 'approved'
      };
      
      // Add to approvedBooks collection (ForgeOfOdin will pick it up)
      const approvedRef = collection(db, 'users', currentUser.uid, 'approvedBooks');
      await addDoc(approvedRef, approvedBook);
      
      // Update barcode counter ONLY if we assigned a new barcode
      if (bookData.barcode.startsWith('PENDING-')) {
        await updateDoc(userRef, {
          lastBarcodeUsed: nextBarcode,
          lastUpdated: new Date()
        });
        console.log(`✅ Book approved with new barcode: ${nextBarcode}`);
      } else {
        console.log(`✅ Book approved with existing barcode: ${bookData.barcode}`);
      }
      
      // Delete from flaggedBooks
      await deleteDoc(flaggedRef);
      
    } catch (error) {
      console.error('Error approving book:', error);
      alert('Failed to approve book: ' + error.message);
    }
  };

  const dismissBook = async (documentId) => {
    try {
      const flaggedRef = doc(db, 'users', currentUser.uid, 'flaggedBooks', documentId);
      await deleteDoc(flaggedRef);
      console.log('Book dismissed');
    } catch (error) {
      console.error('Error dismissing book:', error);
      alert('Failed to dismiss book: ' + error.message);
    }
  };

  const bulkApprove = async () => {
    if (!window.confirm(`Approve all ${filteredItems.length} flagged items?`)) return;

    try {
      // Approve sequentially to prevent barcode race condition
      for (const item of filteredItems) {
        await approveBook(item.id);
      }
    } catch (error) {
      console.error('Error in bulk approve:', error);
      alert('Failed to approve books: ' + error.message);
    }
  };

  const bulkDismiss = async () => {
    if (!window.confirm(`Dismiss all ${filteredItems.length} flagged items?`)) return;
    
    try {
      const promises = filteredItems.map(item => dismissBook(item.id));
      await Promise.all(promises);
    } catch (error) {
      console.error('Error in bulk dismiss:', error);
      alert('Failed to dismiss books: ' + error.message);
    }
  };

  // Enhanced getContentFlagBadges function to include duplicate info
  const getContentFlagBadges = (reviewFlags, item) => {
    if (!reviewFlags || reviewFlags.length === 0) return null;
    
    const flagColors = {
      'Over Grade 8': 'bg-red-100 text-red-800',
      'LGBTQ Content': 'bg-orange-100 text-orange-800',
      'Mature Themes': 'bg-pink-100 text-pink-800',
      'Violence': 'bg-red-100 text-red-800',
      'Scary Content': 'bg-purple-100 text-purple-800',
      'Religious Content': 'bg-blue-100 text-blue-800',
      'Language': 'bg-yellow-100 text-yellow-800',
      'Potential Duplicate': 'bg-blue-100 text-blue-800'
    };

    return (
      <div className="flex flex-wrap gap-1 mt-2">
        {reviewFlags.map((flag, index) => (
          <span 
            key={index} 
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${flagColors[flag] || 'bg-slate-100 text-slate-800'}`}
          >
            {flag}
          </span>
        ))}
      </div>
    );
  };

  // Get severity color for LGBTQ content
  const getLgbtqSeverityColor = (severity) => {
    switch (severity) {
      case 'MINIMAL': return 'text-green-600';
      case 'SECONDARY': return 'text-yellow-600';
      case 'PROMINENT': return 'text-orange-600';
      case 'CENTRAL': return 'text-red-600';
      default: return 'text-slate-600';
    }
  };

  // Generate dynamic filtering criteria based on actual settings
  const getActiveFilteringCriteria = () => {
    const criteria = [];
    
    if (contentFilterSettings.flagOverGrade8) {
      criteria.push('Over Grade 8 content');
    }
    if (contentFilterSettings.flagSexualContent) {
      criteria.push('Sexual/romantic themes');
    }
    if (contentFilterSettings.flagControversialTopics) {
      criteria.push('Controversial topics (LGBTQ, religious)');
    }
    if (contentFilterSettings.flagViolence) {
      criteria.push('Violence & weapons');
    }
    if (contentFilterSettings.flagLanguage) {
      criteria.push('Language concerns');
    }
    if (contentFilterSettings.enableDuplicateChecking) {
      criteria.push('Potential duplicates');
    }
    
    return criteria;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white shadow-xl border-b border-purple-700/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <button onClick={() => navigate('/asgard')} className="text-lg sm:text-2xl font-bold hover:text-purple-200 tracking-wider">
                HEIMDL
              </button>
              <span className="text-purple-300 hidden sm:inline">|</span>
              <span className="text-purple-200 hidden sm:inline">Loki's Chamber</span>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="text-right">
                <p className="text-sm text-purple-300">Content Review Queue</p>
                <p className="text-xl font-bold">{flaggedItems.length}</p>
              </div>
              <button
                onClick={handleLogout}
                className="bg-purple-700 hover:bg-purple-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors text-sm sm:text-base"
              >
                Leave Asgard
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="bg-slate-200 rounded-xl shadow-xl p-6 border border-slate-300">
            <h1 className="text-2xl font-bold mb-4 text-slate-800">Loki's Chamber</h1>
            <p className="text-slate-600 mb-6">Review content flagged by the Forge of Odin's AI filtering system</p>
            
            {/* Navigation Links */}
            <div className="mb-6 flex flex-wrap gap-2 sm:gap-4">
              <button
                onClick={() => navigate('/forge')}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm"
              >
                Back to Forge
              </button>
              <button
                onClick={() => navigate('/yggdrasil')}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                Content Settings
              </button>
              <button
                onClick={() => navigate('/allsight')}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm"
              >
                Browse All Books
              </button>
            </div>
            
            {/* Search and Bulk Actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Search Flagged Items:
                </label>
                <input
                  type="text"
                  placeholder="Title, author, subject..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={bulkApprove}
                  disabled={filteredItems.length === 0}
                  className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-slate-400 text-sm"
                >
                  Approve All
                </button>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={bulkDismiss}
                  disabled={filteredItems.length === 0}
                  className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:bg-slate-400 text-sm"
                >
                  Dismiss All
                </button>
              </div>
            </div>

            {/* Dynamic Content Filtering Info */}
            {(contentFilterSettings.enableContentFiltering || contentFilterSettings.enableDuplicateChecking) && (
              <div className="bg-gradient-to-r from-red-100 to-orange-100 rounded-lg p-4 mb-6 border border-red-200">
                <h3 className="font-bold text-red-800 mb-2">Active Filtering Criteria</h3>
                {getActiveFilteringCriteria().length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-red-700">
                    {getActiveFilteringCriteria().map((criteria, index) => (
                      <div key={index}>• {criteria}</div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-red-700">No content filters are currently enabled.</p>
                )}
                <p className="text-xs text-red-600 mt-2">
                  Configure filtering criteria in Yggdrasil settings
                </p>
              </div>
            )}

            {!contentFilterSettings.enableContentFiltering && !contentFilterSettings.enableDuplicateChecking && (
              <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded-lg mb-6">
                <strong>Content Filtering Disabled:</strong> No books will be flagged for review. Enable filtering in Yggdrasil settings.
                <button 
                  onClick={() => navigate('/yggdrasil')}
                  className="ml-3 bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700"
                >
                  Enable in Settings
                </button>
              </div>
            )}
            
            {/* Barcode Assignment Info */}
            <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg mb-6">
              <h4 className="font-bold mb-1">📋 Barcode Assignment</h4>
              <p className="text-sm">
                Flagged books receive temporary IDs. Real barcodes are assigned only when you approve them, 
                ensuring no barcode gaps when books are dismissed.
              </p>
            </div>
          </div>

          {/* Flagged Items List */}
          {loading ? (
            <div className="bg-slate-200 rounded-xl shadow-xl p-8 border border-slate-300 text-center">
              <p className="text-slate-600">Loading flagged items...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="bg-slate-200 rounded-xl shadow-xl p-8 border border-slate-300 text-center">
              <div className="space-y-4">
                <p className="text-slate-600 text-lg">
                  {flaggedItems.length === 0 
                    ? 'No items need review!' 
                    : 'No items match your search.'
                  }
                </p>
                {flaggedItems.length === 0 && (
                  <div>
                    <p className="text-slate-500 text-sm mb-4">
                      Items will appear here when the Forge of Odin's AI detects content that needs librarian review based on your filtering settings.
                    </p>
                    <div className="space-x-3">
                      <button 
                        onClick={() => navigate('/forge')}
                        className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700"
                      >
                        Visit the Forge
                      </button>
                      <button 
                        onClick={() => navigate('/yggdrasil')}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                      >
                        Adjust Settings
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-slate-200 rounded-xl shadow-xl border border-slate-300">
              <div className="px-6 py-4 border-b border-slate-300 flex justify-between items-center bg-gradient-to-r from-slate-300 to-slate-250 rounded-t-xl">
                <h2 className="text-xl font-bold text-slate-800">
                  Content Review Queue ({filteredItems.length} items)
                </h2>
                <div className="text-sm text-slate-600">
                  {searchTerm && `Showing ${filteredItems.length} of ${flaggedItems.length} flagged items`}
                </div>
              </div>
              
              <div className="divide-y divide-slate-300 max-h-96 overflow-y-auto">
                {filteredItems.map((item) => (
                  <div key={item.id} className="px-6 py-4 hover:bg-slate-150">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-medium text-slate-800">{item.title}</h3>
                            <p className="text-sm text-slate-600">{item.author}</p>
                            <p className="text-xs text-slate-500 font-mono">
                              {item.barcode.startsWith('PENDING-') ? (
                                <span className="text-amber-600">⏳ Barcode will be assigned on approval</span>
                              ) : (
                                `Barcode: ${item.barcode}`
                              )}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Target: {item.targetAudience}</p>
                            <p className="text-xs text-slate-500">Location: {item.location}</p>
                            {item.series && (
                              <p className="text-xs text-indigo-500">Series: {item.series}{item.seriesNumber ? ` #${item.seriesNumber}` : ''}</p>
                            )}
                            {item.subjectPerson && (
                              <p className="text-xs text-teal-500">Subject: {item.subjectPerson}</p>
                            )}
                            {item.flaggedAt && (
                              <p className="text-xs text-slate-400">
                                Flagged: {new Date(item.flaggedAt.seconds * 1000).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* Content Flags */}
                        {getContentFlagBadges(item.reviewFlags, item)}
                        
                        {/* Duplicate Information Display */}
                        {item.isDuplicateFlag && item.duplicateInfo && (
                          <div className="mt-2 bg-blue-50 rounded-lg p-3 border border-blue-200">
                            <h4 className="text-xs font-bold text-blue-800 mb-1">Potential Duplicate Found</h4>
                            <div className="text-xs text-blue-700 space-y-1">
                              <p><strong>Search Method:</strong> {item.duplicateInfo.searchMethod}</p>
                              <p><strong>Results Found:</strong> {item.duplicateInfo.resultCount} matches in OPAC</p>
                              {item.duplicateInfo.existingTitle && (
                                <p><strong>Existing Title:</strong> {item.duplicateInfo.existingTitle}</p>
                              )}
                              <div className="mt-2 space-x-2">
                                <a 
                                  href={item.duplicateInfo.searchUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                >
                                  View in OPAC
                                </a>
                              </div>
                            </div>
                            <div className="mt-2 text-xs text-blue-600">
                              <strong>Librarian Decision Required:</strong>
                              <ul className="list-disc list-inside mt-1 space-y-0.5">
                                <li><strong>Approve:</strong> Different edition, additional copy, or false positive</li>
                                <li><strong>Dismiss:</strong> True duplicate, already in collection</li>
                              </ul>
                            </div>
                          </div>
                        )}
                        
                        {/* Enhanced LGBTQ Details with Severity */}
                        {item.lgbtqDetails && (
                          <div className="mt-2">
                            <p className="text-xs text-orange-600">
                              LGBTQ Content: {item.lgbtqDetails}
                            </p>
                            {item.lgbtqSeverity && (
                              <p className={`text-xs font-medium ${getLgbtqSeverityColor(item.lgbtqSeverity)}`}>
                                Severity: {item.lgbtqSeverity}
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* Book Details */}
                        {item.subjects && item.subjects.length > 0 && (
                          <p className="text-xs text-blue-600 mt-2">
                            Subjects: {item.subjects.slice(0, 3).join(', ')}
                          </p>
                        )}
                        
                        {item.generalNote && (
                          <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                            {item.generalNote.substring(0, 150)}...
                          </p>
                        )}
                      </div>
                      
                      <div className="flex flex-col space-y-2 ml-4">
                        <button
                          onClick={() => approveBook(item.id)}
                          className="bg-green-600 text-white px-4 py-1 rounded text-sm hover:bg-green-700 whitespace-nowrap"
                        >
                          {item.isDuplicateFlag ? 'Approve (Add Copy)' : 'Approve & Add to Batch'}
                        </button>
                        <button
                          onClick={() => dismissBook(item.id)}
                          className="bg-red-600 text-white px-4 py-1 rounded text-sm hover:bg-red-700 whitespace-nowrap"
                        >
                          {item.isDuplicateFlag ? 'Dismiss (Duplicate)' : 'Dismiss'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default LokisChamber;